/**
 * Payment Result Handler Composable
 *
 * Handles payment success/cancel URL parameters after Stripe Checkout redirect.
 * Call this in pages that receive payment redirects (e.g., settings page).
 */

export const usePaymentResult = () => {
  const route = useRoute()
  const router = useRouter()
  const toast = useToast()
  const { checkSessionStatus } = useStripeCheckout()

  const isCheckingPayment = ref(false)
  const paymentResult = ref<{
    status: 'success' | 'cancelled' | 'pending' | null
    sessionId?: string
    order?: {
      id: string
      total: number
      items: Array<{
        productName: string
        tierLabel: string
        quantity: number
        unitAmount: number
      }>
    }
  }>({ status: null })

  /**
   * Check URL for payment result parameters and handle accordingly
   * Call this in onMounted
   */
  const handlePaymentResult = async () => {
    const paymentParam = route.query.payment as string
    const sessionId = route.query.session_id as string

    if (!paymentParam) {
      return
    }

    if (paymentParam === 'success' && sessionId) {
      isCheckingPayment.value = true
      paymentResult.value = { status: 'pending', sessionId }

      try {
        const result = await checkSessionStatus(sessionId)

        if (result.status === 'completed') {
          paymentResult.value = {
            status: 'success',
            sessionId,
            order: result.order,
          }

          toast.add({
            severity: 'success',
            summary: 'Payment Successful',
            detail: 'Thank you for your purchase! Check your email for confirmation.',
            life: 5000,
          })
        }
        else if (result.status === 'expired') {
          paymentResult.value = { status: 'cancelled', sessionId }
          toast.add({
            severity: 'warn',
            summary: 'Session Expired',
            detail: 'Your checkout session has expired. Please try again.',
            life: 5000,
          })
        }
        else {
          // Still pending - webhook may not have processed yet
          paymentResult.value = { status: 'pending', sessionId }

          toast.add({
            severity: 'info',
            summary: 'Payment Processing',
            detail: 'Your payment is being processed. This may take a moment.',
            life: 5000,
          })
        }
      }
      catch (err) {
        console.error('Error checking payment status:', err)
        // The status check failed — don't claim success; the webhook is
        // authoritative and the purchase appears once it lands.
        paymentResult.value = { status: 'pending', sessionId }
        toast.add({
          severity: 'info',
          summary: 'Confirming Payment',
          detail: 'We could not confirm your payment yet. If it went through, a confirmation email will arrive shortly — otherwise please check your purchases page.',
          life: 8000,
        })
      }
      finally {
        isCheckingPayment.value = false
      }
    }
    else if (paymentParam === 'cancelled') {
      paymentResult.value = { status: 'cancelled', sessionId }

      // Release whatever the session reserved. Bulk checkouts hold discount cap
      // slots from the moment the session is created, and without this they stay
      // held until Stripe expires the session ~24h later — which looks to a
      // captain like their discount simply stopped working. Best effort: the
      // cancellation itself is not in doubt, so a failure here must not surface.
      if (sessionId) {
        try {
          await $fetch('/api/stripe/cancel-checkout', {
            method: 'POST',
            body: { sessionId },
          })
        }
        catch {
          // Stripe's own expiry remains the backstop.
        }
      }

      toast.add({
        severity: 'info',
        summary: 'Payment Cancelled',
        detail: 'Your payment was cancelled. No charges were made.',
        life: 5000,
      })
    }

    // Clean up URL parameters
    await router.replace({ query: {} })
  }

  return {
    isCheckingPayment: readonly(isCheckingPayment),
    paymentResult: readonly(paymentResult),
    handlePaymentResult,
  }
}
